import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import * as S from './TruncatedTextBlock.styles'

interface TruncatedTextBlockProps {
  contents?: string
  onSeeMoreClick: VoidFunction
  className?: string
}

export const TruncatedTextBlock = ({
  contents,
  onSeeMoreClick,
  className,
}: TruncatedTextBlockProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const textRef = useRef<HTMLDivElement | null>(null)
  const [lines, setLines] = useState<number | undefined>(undefined)
  const [isTruncated, setIsTruncated] = useState(false)
  const [ready, setReady] = useState(false)

  const measure = useCallback(() => {
    const root = rootRef.current
    const text = textRef.current
    if (!root || !text) return

    const parent = root.parentElement
    if (!parent) {
      setLines(undefined)
      setIsTruncated(false)
      setReady(true)
      return
    }

    const getAvailableHeight = (el: HTMLElement): number => {
      const ch = el.clientHeight
      if (ch > 0) return ch
      const cs = window.getComputedStyle(el)
      const maxH = parseFloat(cs.maxHeight || '0')
      if (!Number.isNaN(maxH) && maxH > 0 && maxH < 1e6) return maxH
      const h = parseFloat(cs.height || '0')
      return !Number.isNaN(h) && h > 0 ? h : ch
    }

    const availableHeight = Math.floor(getAvailableHeight(parent))

    // Compute line-height
    const cs = window.getComputedStyle(text)
    let lh = parseFloat(cs.lineHeight)
    if (Number.isNaN(lh)) {
      const fs = parseFloat(cs.fontSize) || 16
      lh = Math.round(fs * 1.2)
    }
    if (!lh || !Number.isFinite(lh) || lh <= 0) {
      // Fallback to avoid division by zero
      lh = 16
    }

    if (!availableHeight || !Number.isFinite(availableHeight) || availableHeight <= 0) {
      setLines(undefined)
      setIsTruncated(false)
      setReady(true)
      return
    }

    const maxLines = Math.max(1, Math.floor(availableHeight / lh) - 1)

    // Measure full content height without clamping using inline override
    const prevClamp = text.style.getPropertyValue('-webkit-line-clamp')
    text.style.setProperty('-webkit-line-clamp', 'unset')
    const fullHeight = text.scrollHeight
    // Restore previous inline style
    if (prevClamp) {
      text.style.setProperty('-webkit-line-clamp', prevClamp)
    } else {
      text.style.removeProperty('-webkit-line-clamp')
    }

    if (fullHeight > availableHeight + 1) {
      setLines(maxLines)
      setIsTruncated(true)
    } else {
      setLines(undefined)
      setIsTruncated(false)
    }
    setReady(true)
  }, [])

  useLayoutEffect(() => {
    measure()
    const root = rootRef.current
    const parent = root?.parentElement
    const text = textRef.current

    if (!parent || !text) return

    const ro = new ResizeObserver(() => measure())
    ro.observe(parent)
    ro.observe(text)
    return () => {
      ro.disconnect()
    }
  }, [measure])

  useEffect(() => {
    // Re-measure when contents change
    measure()
  }, [contents, measure])

  return (
    <S.Root ref={rootRef} className={className}>
      <S.Text ref={textRef} $lines={lines} $hidden={!ready}>
        {contents}
      </S.Text>
      {isTruncated && (
        <S.SeeMore onClick={onSeeMoreClick} aria-label="See more">
          See more
        </S.SeeMore>
      )}
    </S.Root>
  )
}
